import { buildChildrenMap, getDescendantIds } from './tree';

export const DEFAULT_SIZE = { w: 140, h: 44 };
export const MIN_ZOOM = 0.2;
export const MAX_ZOOM = 2.5;
export const SHAPE_MIN = { width: 120, height: 72 };
export const SHAPE_MAX = 4000;

const H_GAP = 64;
const V_GAP = 16;
const COLLISION_PADDING = 10;

const sizeOf = (sizes, id) => sizes[id] ?? DEFAULT_SIZE;

export const clampZoom = (k) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, k));

export const tidyLayout = (nodes, sizes) => {
  const root = nodes.find((node) => node.parentId == null);
  if (!root) return {};

  const children = buildChildrenMap(nodes);
  const heights = new Map();

  const measure = (id) => {
    const kids = children.get(id) ?? [];
    const own = sizeOf(sizes, id).h;
    const stacked = kids.reduce((sum, kid) => sum + measure(kid.id), 0) + V_GAP * Math.max(0, kids.length - 1);
    const height = Math.max(own, stacked);
    heights.set(id, height);
    return height;
  };
  measure(root.id);

  const positions = { [root.id]: { x: 0, y: 0 } };

  const placeChildren = (parentId, kids, dir, centerY) => {
    const parent = positions[parentId];
    const parentWidth = sizeOf(sizes, parentId).w;
    const total = kids.reduce((sum, kid) => sum + heights.get(kid.id), 0) + V_GAP * Math.max(0, kids.length - 1);
    let top = centerY - total / 2;

    kids.forEach((kid) => {
      const height = heights.get(kid.id);
      const cy = top + height / 2;
      positions[kid.id] = { x: parent.x + dir * (parentWidth / 2 + H_GAP + sizeOf(sizes, kid.id).w / 2), y: cy };
      placeChildren(kid.id, children.get(kid.id) ?? [], dir, cy);
      top += height + V_GAP;
    });
  };

  const sides = { right: [], left: [] };
  const totals = { right: 0, left: 0 };
  (children.get(root.id) ?? []).forEach((kid) => {
    const side = totals.right <= totals.left ? 'right' : 'left';
    sides[side].push(kid);
    totals[side] += heights.get(kid.id) + V_GAP;
  });
  placeChildren(root.id, sides.right, 1, 0);
  placeChildren(root.id, sides.left, -1, 0);

  return positions;
};

const collides = (x, y, w, h, node, sizes) => {
  const size = sizeOf(sizes, node.id);
  return (
    Math.abs(x - node.x) < (w + size.w) / 2 + COLLISION_PADDING &&
    Math.abs(y - node.y) < (h + size.h) / 2 + COLLISION_PADDING
  );
};

export const nextChildPosition = (nodes, visibleNodes, parentId, sizes) => {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const parent = byId.get(parentId);
  const childrenMap = buildChildrenMap(nodes);
  const kids = childrenMap.get(parentId) ?? [];
  const isRoot = parent.parentId == null;
  const { w, h } = DEFAULT_SIZE;

  let dir;
  if (isRoot) {
    const onRight = kids.filter((kid) => kid.x >= parent.x).length;
    dir = onRight <= kids.length - onRight ? 1 : -1;
  } else {
    dir = parent.x >= byId.get(parent.parentId).x ? 1 : -1;
  }

  const sameSide = kids.filter((kid) => !isRoot || (dir === 1 ? kid.x >= parent.x : kid.x < parent.x));
  const bottoms = sameSide.flatMap((kid) =>
    [kid, ...[...getDescendantIds(nodes, kid.id, childrenMap)].map((id) => byId.get(id))].map(
      (node) => node.y + sizeOf(sizes, node.id).h / 2
    )
  );

  const x = parent.x + dir * (sizeOf(sizes, parentId).w / 2 + H_GAP + w / 2);
  let y = bottoms.length ? Math.max(...bottoms) + V_GAP + h / 2 : parent.y;

  for (let attempt = 0; attempt < 200 && visibleNodes.some((node) => collides(x, y, w, h, node, sizes)); attempt++) {
    y += h + V_GAP;
  }
  return { x, y };
};

export const computeFitView = (nodes, sizes, viewport, padding = { top: 56, right: 40, bottom: 56, left: 40 }, shapes = []) => {
  if (!nodes.length || !viewport.w || !viewport.h) return null;

  const nodeBounds = nodes.reduce(
    (acc, node) => {
      const { w, h } = sizeOf(sizes, node.id);
      return {
        minX: Math.min(acc.minX, node.x - w / 2),
        maxX: Math.max(acc.maxX, node.x + w / 2),
        minY: Math.min(acc.minY, node.y - h / 2),
        maxY: Math.max(acc.maxY, node.y + h / 2),
      };
    },
    { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity }
  );
  const bounds = shapes.reduce(
    (acc, shape) => ({
      minX: Math.min(acc.minX, shape.x),
      maxX: Math.max(acc.maxX, shape.x + shape.width),
      minY: Math.min(acc.minY, shape.y),
      maxY: Math.max(acc.maxY, shape.y + shape.height),
    }),
    nodeBounds
  );

  const availableW = viewport.w - padding.left - padding.right;
  const availableH = viewport.h - padding.top - padding.bottom;
  const k = Math.min(1, clampZoom(Math.min(availableW / (bounds.maxX - bounds.minX), availableH / (bounds.maxY - bounds.minY))));
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cy = (bounds.minY + bounds.maxY) / 2;

  return {
    k,
    x: padding.left + availableW / 2 - cx * k,
    y: padding.top + availableH / 2 - cy * k,
  };
};

export const edgePath = (parent, child, sizes) => {
  const p = sizeOf(sizes, parent.id);
  const c = sizeOf(sizes, child.id);
  const horizontalGap = Math.abs(child.x - parent.x) - (p.w + c.w) / 2;

  if (horizontalGap >= 16) {
    const dir = child.x >= parent.x ? 1 : -1;
    const sx = parent.x + (dir * p.w) / 2;
    const ex = child.x - (dir * c.w) / 2;
    const bend = Math.max(28, Math.abs(ex - sx) / 2);
    return `M ${sx} ${parent.y} C ${sx + dir * bend} ${parent.y}, ${ex - dir * bend} ${child.y}, ${ex} ${child.y}`;
  }

  const dir = child.y >= parent.y ? 1 : -1;
  const sy = parent.y + (dir * p.h) / 2;
  const ey = child.y - (dir * c.h) / 2;
  const bend = Math.max(24, Math.abs(ey - sy) / 2);
  return `M ${parent.x} ${sy} C ${parent.x} ${sy + dir * bend}, ${child.x} ${ey - dir * bend}, ${child.x} ${ey}`;
};
